import React, { Component, ReactNode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import {
  getErrorMessage,
  isRecoverableChunkLoadError,
  scheduleChunkLoadRecovery,
} from './lib/chunkLoadRecovery';

// Debug log capture - MUST be first to capture all console output
import { installConsoleInterceptor } from './lib/debugLogBuffer';
installConsoleInterceptor();

// Stub providers for wallet-disabled mode - MUST be separate files with NO wagmi imports
// This avoids loading wagmi in the boot chunk, preventing TDZ crash
import { WalletTxProviderStub } from './contexts/WalletTxContextStub';
import { WalletActionsProviderStub } from './contexts/WalletActionsProviderStub';

// Wallet kill switch - controlled via VITE_DISABLE_WALLET environment variable
// When enabled, wallet SDK is stubbed at build time (vite.config.ts) and runtime uses NoWalletWrapper
const WALLET_DISABLED = import.meta.env.VITE_DISABLE_WALLET === '1' || import.meta.env.VITE_DISABLE_WALLET === 'true';

if (WALLET_DISABLED) {
  console.warn('⚠️ [WALLET] Wallet providers DISABLED via VITE_DISABLE_WALLET');
}

function maybeRecoverFromChunkLoadError(error: unknown): boolean {
  if (!isRecoverableChunkLoadError(error)) return false;
  return scheduleChunkLoadRecovery();
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const error = event.error || event.message;
    if (maybeRecoverFromChunkLoadError(error)) {
      event.preventDefault();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (maybeRecoverFromChunkLoadError(event.reason)) {
      event.preventDefault();
    }
  });
}

type LazyImport<T> = () => Promise<T>;

function timedLazyImport<T>(label: string, importer: LazyImport<T>): Promise<T> {
  const startedAt = Date.now();
  let settled = false;
  const promise = importer()
    .then((module) => {
      settled = true;
      if (import.meta.env.DEV) {
        console.debug(`[PoolParty boot] ${label} loaded in ${Date.now() - startedAt}ms`);
      }
      return module;
    })
    .catch((error) => {
      settled = true;
      console.error(`[PoolParty boot] ${label} failed to load`, error);
      throw error;
    });

  if (import.meta.env.DEV) {
    window.setTimeout(() => {
      if (!settled) {
        console.warn(`[PoolParty boot] ${label} still loading after ${Date.now() - startedAt}ms`);
      }
    }, 12000);
  }

  return promise;
}

function preloadCurrentRoute() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  if (hash.includes('/admin/lazy-river-2')) {
    void timedLazyImport('route:admin-lazy-river-2', () => import('./components/AdminLazyRiver2Page'));
    return;
  }
  if (/\/app\/channel\/[^/]+\/control/.test(hash)) {
    void timedLazyImport('route:channel-control-room', () => import('./components/channel/ControlRoom'));
    return;
  }
  if (/\/app\/channel\/[^/?#]+(?:[?#].*)?$/.test(hash)) {
    void timedLazyImport('route:channel-page', () => import('./components/channel/ChannelPage'));
  }
}

// Lazy-load BOTH App and WalletProviders to avoid TDZ initialization errors from @reown/appkit.
// Start the promises before React renders so configured Vercel dev cold-starts do not serialize
// "wallet providers first, app route second" and leave the browser on the root loading fallback.
const walletProvidersPromise = timedLazyImport('wallet-providers', () => import('./components/WalletProviders'));
const appPromise = timedLazyImport('app-shell', () => import('./App'));
preloadCurrentRoute();

const WalletProviders = lazy(() => walletProvidersPromise);
const App = lazy(() => appPromise);

// Wrapper when wallet is disabled - provides stub contexts only
// NO wagmi imports here to avoid loading WalletConnect/Reown SDK in the boot chunk
function NoWalletWrapper({ children }: { children: React.ReactNode }) {
  return (
    <WalletTxProviderStub>
      <WalletActionsProviderStub>
        {children}
      </WalletActionsProviderStub>
    </WalletTxProviderStub>
  );
}

// Simple loading fallback - shown briefly while wallet SDK loads
function WalletLoadingFallback() {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#1a1a1a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#FF00FF',
      fontFamily: 'system-ui, sans-serif',
      fontSize: '1.25rem',
      fontWeight: 'bold',
    }}>
      Loading PoolParty...
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
  errorInfo: any;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: any): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("PoolParty Error Boundary:", error, errorInfo);
    maybeRecoverFromChunkLoadError(error);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const isChunkLoadError = isRecoverableChunkLoadError(this.state.error);
      return (
        <div style={{ padding: '2rem', backgroundColor: '#1a1a1a', color: '#ff0099', minHeight: '100vh', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem' }}>{isChunkLoadError ? 'PoolParty Needs a Refresh' : 'PoolParty Crashed'}</h1>
          {isChunkLoadError && (
            <p style={{ color: '#fef3c7', maxWidth: '720px', textAlign: 'center', lineHeight: 1.5, marginBottom: '1rem' }}>
              This browser has an older app shell and tried to load a production file that has changed. Reloading will fetch the current PoolParty build.
            </p>
          )}
          <div style={{ backgroundColor: '#000', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #333', color: '#f87171', maxWidth: '800px', width: '100%', overflow: 'auto' }}>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{getErrorMessage(this.state.error)}</pre>
            {!isChunkLoadError && this.state.errorInfo?.componentStack && (
              <pre style={{ marginTop: '1rem', whiteSpace: 'pre-wrap', fontFamily: 'monospace', color: '#fbbf24' }}>
                {this.state.errorInfo.componentStack}
              </pre>
            )}
          </div>
          <button
            onClick={() => {
              if (isChunkLoadError) {
                scheduleChunkLoadRecovery({ force: true, delayMs: 0 });
                return;
              }
              window.location.reload();
            }}
            style={{ marginTop: '2rem', padding: '0.75rem 1.5rem', backgroundColor: '#4F46E5', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 'bold' }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

console.log("PoolParty: Mounting App...");

const root = createRoot(rootElement);

// Use kill switch to bypass wallet providers if needed
const Wrapper = WALLET_DISABLED ? NoWalletWrapper : WalletProviders;

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <Suspense fallback={<WalletLoadingFallback />}>
        <Wrapper>
          <App />
        </Wrapper>
      </Suspense>
    </ErrorBoundary>
  </React.StrictMode>
);
